import * as React from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";

import * as Yup from "yup";
import { Form, Formik } from "formik";
import { VALIDATION_CONSTANTS } from "../../../utils/validation-constants/validationConstants";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import Button from "../../../component-lib/Molecules/button/Button";
import Input from "../../../component-lib/Atoms/Input/Input";
import ContactInput from "../../../component-lib/Atoms/Input/ContactInput";
import { normalizeContactNumber } from "../../../utils/phoneNumberFormatter";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import EventEHRCheckbox from "../../../component-lib/Molecules/checkbox/EventEHRCheckbox";
import { ADD_FACILITY_DETAILS, SPECIALTY_TYPE } from "../constants";

// ADD_FACILITY_FORM

interface AddFacilityProps {
  defaultSelectedData?: any;
  handleOnSubmittedData?: (formData: any) => void;
}

const AddFacility = ({
  defaultSelectedData,
  handleOnSubmittedData,
}: AddFacilityProps) => {
  const validationSchema = Yup.object().shape({
    [ADD_FACILITY_DETAILS.FACILITY_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER_WITH_LIMIT_40,
        "Facility name must contain only letters and spaces and must not exceed 40 characters."
      )
      .required("Facility name is required"),
    [ADD_FACILITY_DETAILS.CONTACT_PERSON]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER_WITH_LIMIT_40,
        "Facility admin name must contain only letters and spaces and must not exceed 40 characters."
      )
      .required("Facility admin name is required"),
    [ADD_FACILITY_DETAILS.CONTACT_NUMBER]: Yup.string()
      .matches(VALIDATION_CONSTANTS.CONTACT_NUMBER, "Invalid contact number")
      .required("Contact number is required"),
    [ADD_FACILITY_DETAILS.EMAIL]: Yup.string()
      // .email("Enter a valid email address")
      .matches(VALIDATION_CONSTANTS.EMAIL, "Enter a valid email address")
      .required("Email is required"),

    [ADD_FACILITY_DETAILS?.CMO_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER_WITH_LIMIT_40,
        "CMO name must contain only letters and spaces and must not exceed 40 characters."
      )
      .required("CMO name is required"),

    [ADD_FACILITY_DETAILS?.CNO_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER_WITH_LIMIT_40,
        "CNO name must contain only letters and spaces and must not exceed 40 characters."
      )
      .required("CNO name is required"),

    [ADD_FACILITY_DETAILS?.CAO_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER_WITH_LIMIT_40,
        "CAO name must contain only letters and spaces and must not exceed 40 characters."
      )
      .required("CAO name is required"),
    [ADD_FACILITY_DETAILS.CMO_CONTACT_NUMBER]: Yup.string()
      .matches(VALIDATION_CONSTANTS.CONTACT_NUMBER, "Invalid contact number")
      .required("CMO Contact number is required"),

    [ADD_FACILITY_DETAILS.CNO_CONTACT_NUMBER]: Yup.string()
      .matches(VALIDATION_CONSTANTS.CONTACT_NUMBER, "Invalid contact number")
      .required("CNO Contact number is required"),

    [ADD_FACILITY_DETAILS.CAO_CONTACT_NUMBER]: Yup.string()
      .matches(VALIDATION_CONSTANTS.CONTACT_NUMBER, "Invalid contact number")
      .required("CAO Contact number is required"),

    [ADD_FACILITY_DETAILS.SPECIALITY_TYPE]: Yup.string().required(
      "Speciality type is required"
    ),
    [ADD_FACILITY_DETAILS.PLACE_OF_SERVICES]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER_WITH_LIMIT_40,
        "Place of services must contain only letters and spaces and must not exceed 40 characters."
      )
      .required("Place of services is required"),

    [ADD_FACILITY_DETAILS.WEBSITE]: Yup.string()
      // .url('Enter a valid URL') // for http url
      .matches(
        VALIDATION_CONSTANTS.WEBSITE,
        "Enter a valid website (e.g., www.example.com)"
      )
      .required("Website is required"),

    [ADD_FACILITY_DETAILS.FAX_ID]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.LIMIT_20,
        "Fax ID must not exceed 20 digits."
      )
      .required("Fax id is required"),
    [ADD_FACILITY_DETAILS.FAX_NUMBER]: Yup.string()
      // .matches(
      //   VALIDATION_CONSTANTS.ID,
      //   "Fax number must contain only letters and numbers"
      // )
      .matches(VALIDATION_CONSTANTS.CONTACT_NUMBER, "Invalid Fax number")

      .required("Fax number is required"),
    [ADD_FACILITY_DETAILS.ADDRESS_1]: Yup.string()
      // .matches(
      //   VALIDATION_CONSTANTS.ADDRESS,
      //   "Address can only contain letters and numbers"
      // )
      // .min(5, "Address must be at least 5 characters")
      .max(50, "Address cannot exceed 50 characters")
      .required("Address  is required"),
    [ADD_FACILITY_DETAILS.ADDRESS_2]: Yup.string()
      // .matches(
      //   VALIDATION_CONSTANTS.ADDRESS,
      //   "Address can only contain letters and numbers"
      // )
      // .min(5, "Address must be at least 5 characters")
      .max(50, "Address cannot exceed 50 characters"),
    // .required("Address  is required"),
    [ADD_FACILITY_DETAILS.CITY]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER,
        "City can only contain characters"
      )
      .max(50, "City name cannot exceed 50 characters")
      .required("City is required"),
    [ADD_FACILITY_DETAILS.STATE]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.US_STATE,
        "Enter a valid US state abbreviation (e.g., NY)"
      )
      .max(2, "State must be a 2-letter abbreviation")
      .required("State is required"),
    [ADD_FACILITY_DETAILS.COUNTRY]: Yup.string().matches(
      VALIDATION_CONSTANTS.CHARACTER,
      "Country can only contain characters"
    ),
    // .matches(
    //   /^United States$/,
    //   'Country must be "United States"'
    // ),

    // .required("Country is required"),
    [ADD_FACILITY_DETAILS.ZIP_CODE]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.ZIP_CODE,
        "Enter a valid US ZIP code (e.g., 12345 or 12345-6789)"
      )
      .required("Zip code is required"),

    // [ADD_FACILITY_DETAILS.DATE_OF_BIRTH]: Yup.date()
    //   .max(new Date(), "Date of birth cannot be a future date")
    //   .required("Date Of birth is required"),
  });

  const headerStyle = {
    fontWeight: "500",
    fontSize: "16px",
    paddingBottom: "10px",
  };

  const commonGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    // gap: "24px 16px",
  };

  return (
    <Formik
      initialValues={{
        [ADD_FACILITY_DETAILS.FACILITY_NAME]: `${defaultSelectedData?.facility || ""}`,
        [ADD_FACILITY_DETAILS.CMO_NAME]: `${defaultSelectedData?.data?.cmoName || ""}`,
        [ADD_FACILITY_DETAILS.CNO_NAME]: `${defaultSelectedData?.data?.cnoName || ""}`,
        [ADD_FACILITY_DETAILS.CAO_NAME]: `${defaultSelectedData?.data?.caoName || ""}`,
        [ADD_FACILITY_DETAILS.CONTACT_NUMBER]: normalizeContactNumber(
          defaultSelectedData?.data?.contactNumber
        ),
        [ADD_FACILITY_DETAILS.CMO_CONTACT_NUMBER]: normalizeContactNumber(
          defaultSelectedData?.data?.cmoNumber
        ),
        [ADD_FACILITY_DETAILS.CNO_CONTACT_NUMBER]: normalizeContactNumber(
          defaultSelectedData?.data?.cnoNumber
        ),
        [ADD_FACILITY_DETAILS.CAO_CONTACT_NUMBER]: normalizeContactNumber(
          defaultSelectedData?.data?.caoNumber
        ),
        [ADD_FACILITY_DETAILS.CONTACT_PERSON]: `${defaultSelectedData?.data?.contactPerson || ""}`,
        [ADD_FACILITY_DETAILS.PLACE_OF_SERVICES]: `${defaultSelectedData?.data?.poc || ""}`,
        [ADD_FACILITY_DETAILS.SPECIALITY_TYPE]: `${defaultSelectedData?.data?.specialities || ""}`,
        [ADD_FACILITY_DETAILS.EMAIL]: `${defaultSelectedData?.data?.email || ""}`,
        [ADD_FACILITY_DETAILS.WEBSITE]: `${defaultSelectedData?.data?.website || ""}`,
        [ADD_FACILITY_DETAILS.FAX_ID]: `${defaultSelectedData?.data?.faxId || ""}`,
        [ADD_FACILITY_DETAILS.FAX_NUMBER]: `${defaultSelectedData?.data?.faxNumber || ""}`,
        [ADD_FACILITY_DETAILS.IS_BASECAMP]: defaultSelectedData?.data?.isBaseCamp || false,
        [ADD_FACILITY_DETAILS.ADDRESS_1]: `${defaultSelectedData?.data?.address?.addressLine1 || "2550 Jack Furst Drive"}`,
        [ADD_FACILITY_DETAILS.ADDRESS_2]: `${defaultSelectedData?.data?.address?.addressLine2 || ""}`,
        [ADD_FACILITY_DETAILS.CITY]: `${defaultSelectedData?.data?.address?.city || "Glen Jean"}`,
        [ADD_FACILITY_DETAILS.STATE]: `${defaultSelectedData?.data?.address?.state || "WV"}`,
        [ADD_FACILITY_DETAILS.COUNTRY]: `${defaultSelectedData?.data?.address?.country || "United States"}`,
        [ADD_FACILITY_DETAILS.ZIP_CODE]: `${defaultSelectedData?.data?.address?.pinCode || "25846"}`,
      }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        try {
          handleOnSubmittedData && handleOnSubmittedData(values);
          // Handle form data here
          // If needed, call an API or further process the values here
        } catch (error) {
          console.log(error);
        } finally {
          setSubmitting(false); // Reset submitting state
        }
      }}
    >
      {({
        errors,
        submitForm,
        isSubmitting,
        handleReset,
        isValid,
        values,
        touched,
        setFieldValue,
        handleBlur,
        handleSubmit,
      }) => {
        return (
          <Form
            onSubmit={handleSubmit}
            className="h-full flex flex-col justify-between rounded-lg"
          >
            <div className="w-full flex flex-col gap-6 rounded-lg">
              {/* ADDRESS DETAILS */}
              <div>
                <Heading title="Facility Details" customStyle={headerStyle} />
                <div style={commonGridStyle}>
                  <Input
                    // disabled
                    textFieldLabel="Facility Name"
                    name={ADD_FACILITY_DETAILS.FACILITY_NAME}
                    value={values[ADD_FACILITY_DETAILS.FACILITY_NAME]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_FACILITY_DETAILS.FACILITY_NAME,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.FACILITY_NAME] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.FACILITY_NAME])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.FACILITY_NAME]
                        ? errors[ADD_FACILITY_DETAILS.FACILITY_NAME] as string
                        : undefined
                    }
                  />

                  <Input
                    // disabled
                    textFieldLabel="Facility Admin Name"
                    name={ADD_FACILITY_DETAILS.CONTACT_PERSON}
                    value={values[ADD_FACILITY_DETAILS.CONTACT_PERSON]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_FACILITY_DETAILS.CONTACT_PERSON,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.CONTACT_PERSON] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.CONTACT_PERSON])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.CONTACT_PERSON]
                        ? errors[ADD_FACILITY_DETAILS.CONTACT_PERSON] as string
                        : undefined
                    }
                  />
                  <ContactInput
                    // disabled
                    textFieldLabel="Admin Contact Number"
                    name={ADD_FACILITY_DETAILS.CONTACT_NUMBER}
                    value={values[ADD_FACILITY_DETAILS.CONTACT_NUMBER]}
                    onChangeCb={(value) => {
                      setFieldValue(ADD_FACILITY_DETAILS.CONTACT_NUMBER, value);
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.CONTACT_NUMBER] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.CONTACT_NUMBER])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.CONTACT_NUMBER]
                        ? errors[ADD_FACILITY_DETAILS.CONTACT_NUMBER] as string
                        : undefined
                    }
                    isContactNumber={true}
                  />

                  <Input
                    // disabled "Edit Facility"
                    textFieldLabel="Admin Email ID"
                    name={ADD_FACILITY_DETAILS.EMAIL}
                    value={values[ADD_FACILITY_DETAILS.EMAIL]}
                    onChangeCb={(e) => {
                      setFieldValue(ADD_FACILITY_DETAILS.EMAIL, e.target.value);
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.EMAIL] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.EMAIL])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.EMAIL]
                        ? errors[ADD_FACILITY_DETAILS.EMAIL] as string
                        : undefined
                    }
                  />

                  <Input
                    // disabled
                    textFieldLabel="CMO Name"
                    name={ADD_FACILITY_DETAILS.CMO_NAME}
                    value={values[ADD_FACILITY_DETAILS.CMO_NAME]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_FACILITY_DETAILS.CMO_NAME,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.CMO_NAME] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.CMO_NAME])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.CMO_NAME]
                        ? errors[ADD_FACILITY_DETAILS.CMO_NAME] as string
                        : undefined
                    }
                  />
                  <ContactInput
                    // disabled
                    textFieldLabel="CMO Contact Number"
                    name={ADD_FACILITY_DETAILS.CMO_CONTACT_NUMBER}
                    value={values[ADD_FACILITY_DETAILS.CMO_CONTACT_NUMBER]}
                    onChangeCb={(value) => {
                      setFieldValue(
                        ADD_FACILITY_DETAILS.CMO_CONTACT_NUMBER,
                        value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.CMO_CONTACT_NUMBER] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.CMO_CONTACT_NUMBER])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.CMO_CONTACT_NUMBER]
                        ? errors[ADD_FACILITY_DETAILS.CMO_CONTACT_NUMBER] as string
                        : undefined
                    }
                    isContactNumber={true}
                  />

                  <Input
                    // disabled
                    textFieldLabel="CNO Name"
                    name={ADD_FACILITY_DETAILS.CNO_NAME}
                    value={values[ADD_FACILITY_DETAILS.CNO_NAME]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_FACILITY_DETAILS.CNO_NAME,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.CNO_NAME] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.CNO_NAME])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.CNO_NAME]
                        ? errors[ADD_FACILITY_DETAILS.CNO_NAME] as string
                        : undefined
                    }
                  />
                  <ContactInput
                    // disabled
                    textFieldLabel="CNO Contact Number"
                    name={ADD_FACILITY_DETAILS.CNO_CONTACT_NUMBER}
                    value={values[ADD_FACILITY_DETAILS.CNO_CONTACT_NUMBER]}
                    onChangeCb={(value) => {
                      setFieldValue(
                        ADD_FACILITY_DETAILS.CNO_CONTACT_NUMBER,
                        value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.CNO_CONTACT_NUMBER] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.CNO_CONTACT_NUMBER])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.CNO_CONTACT_NUMBER]
                        ? errors[ADD_FACILITY_DETAILS.CNO_CONTACT_NUMBER] as string
                        : undefined
                    }
                    isContactNumber={true}
                  />

                  <Input
                    // disabled
                    textFieldLabel="CAO Name"
                    name={ADD_FACILITY_DETAILS.CAO_NAME}
                    value={values[ADD_FACILITY_DETAILS.CAO_NAME]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_FACILITY_DETAILS.CAO_NAME,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.CAO_NAME] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.CAO_NAME])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.CAO_NAME]
                        ? errors[ADD_FACILITY_DETAILS.CAO_NAME] as string
                        : undefined
                    }
                  />
                  <ContactInput
                    // disabled
                    textFieldLabel="CAO Contact Number"
                    name={ADD_FACILITY_DETAILS.CAO_CONTACT_NUMBER}
                    value={values[ADD_FACILITY_DETAILS.CAO_CONTACT_NUMBER]}
                    onChangeCb={(value) => {
                      setFieldValue(
                        ADD_FACILITY_DETAILS.CAO_CONTACT_NUMBER,
                        value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.CAO_CONTACT_NUMBER] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.CAO_CONTACT_NUMBER])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.CAO_CONTACT_NUMBER]
                        ? errors[ADD_FACILITY_DETAILS.CAO_CONTACT_NUMBER] as string
                        : undefined
                    }
                    isContactNumber={true}
                  />

                  <SelectDropdown
                    label="Speciality Type"
                    value={values[ADD_FACILITY_DETAILS.SPECIALITY_TYPE]}
                    options={SPECIALTY_TYPE}
                    onChangeCb={(e) =>
                      setFieldValue(
                        ADD_FACILITY_DETAILS.SPECIALITY_TYPE,
                        e.target.value
                      )
                    }
                    onBlur={handleBlur}
                    error={
                      touched[ADD_FACILITY_DETAILS.SPECIALITY_TYPE] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.SPECIALITY_TYPE])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.SPECIALITY_TYPE]
                        ? errors[ADD_FACILITY_DETAILS.SPECIALITY_TYPE] as string
                        : undefined
                    }
                  />

                  <Input
                    // disabled
                    textFieldLabel="Place Of Service"
                    name={ADD_FACILITY_DETAILS.PLACE_OF_SERVICES}
                    value={values[ADD_FACILITY_DETAILS.PLACE_OF_SERVICES]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_FACILITY_DETAILS.PLACE_OF_SERVICES,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.PLACE_OF_SERVICES] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.PLACE_OF_SERVICES])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.PLACE_OF_SERVICES]
                        ? errors[ADD_FACILITY_DETAILS.PLACE_OF_SERVICES] as string
                        : undefined
                    }
                  />

                  <Input
                    // disabled
                    textFieldLabel="Website"
                    name={ADD_FACILITY_DETAILS.WEBSITE}
                    value={values[ADD_FACILITY_DETAILS.WEBSITE]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_FACILITY_DETAILS.WEBSITE,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.WEBSITE] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.WEBSITE])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.WEBSITE]
                        ? errors[ADD_FACILITY_DETAILS.WEBSITE] as string
                        : undefined
                    }
                  />

                  <Input
                    // disabled
                    textFieldLabel="Fax ID"
                    type="number"
                    name={ADD_FACILITY_DETAILS.FAX_ID}
                    value={values[ADD_FACILITY_DETAILS.FAX_ID]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_FACILITY_DETAILS.FAX_ID,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.FAX_ID] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.FAX_ID])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.FAX_ID]
                        ? errors[ADD_FACILITY_DETAILS.FAX_ID] as string
                        : undefined
                    }
                  />

                  <ContactInput
                    // disabled
                    textFieldLabel="Fax Number"
                    name={ADD_FACILITY_DETAILS.FAX_NUMBER}
                    value={values[ADD_FACILITY_DETAILS.FAX_NUMBER]}
                    onChangeCb={(value) => {
                      setFieldValue(ADD_FACILITY_DETAILS.FAX_NUMBER, value);
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.FAX_NUMBER] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.FAX_NUMBER])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.FAX_NUMBER]
                        ? errors[ADD_FACILITY_DETAILS.FAX_NUMBER] as string
                        : undefined
                    }
                    isContactNumber={true}
                  />

                  <div style={{ display: "flex", alignItems: "center", marginTop: "8px" }}>
                    <EventEHRCheckbox
                      optionList={[{ label: "Is Basecamp", value: "isBasecamp" }]}
                      initialValues={values[ADD_FACILITY_DETAILS.IS_BASECAMP] ? ["isBasecamp"] : []}
                      onChangeCb={(selectedValues) => {
                        setFieldValue(ADD_FACILITY_DETAILS.IS_BASECAMP, selectedValues.includes("isBasecamp"));
                      }}
                    />
                  </div>
                </div>
              </div>
              <div>
                <Heading title="Address" customStyle={headerStyle} />
                <div style={commonGridStyle}>
                  <Input
                    // disabled
                    maxLength={50}
                    textFieldLabel="Address 1"
                    name={ADD_FACILITY_DETAILS.ADDRESS_1}
                    value={values[ADD_FACILITY_DETAILS.ADDRESS_1]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_FACILITY_DETAILS.ADDRESS_1,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.ADDRESS_1] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.ADDRESS_1])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.ADDRESS_1]
                        ? errors[ADD_FACILITY_DETAILS.ADDRESS_1] as string
                        : undefined
                    }
                  />

                  <Input
                    // disabled
                    maxLength={50}
                    textFieldLabel="Address 2"
                    name={ADD_FACILITY_DETAILS.ADDRESS_2}
                    value={values[ADD_FACILITY_DETAILS.ADDRESS_2]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_FACILITY_DETAILS.ADDRESS_2,
                        e.target.value
                      );
                    }}
                    size="small"
                    onBlur={handleBlur}
                    error={
                      touched[ADD_FACILITY_DETAILS.ADDRESS_2] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.ADDRESS_2])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.ADDRESS_2]
                        ? errors[ADD_FACILITY_DETAILS.ADDRESS_2] as string
                        : undefined
                    }
                  />

                  <Input
                    // disabled
                    textFieldLabel="City"
                    name={ADD_FACILITY_DETAILS.CITY}
                    value={values[ADD_FACILITY_DETAILS.CITY]}
                    onChangeCb={(e) => {
                      setFieldValue(ADD_FACILITY_DETAILS.CITY, e.target.value);
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.CITY] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.CITY])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.CITY]
                        ? errors[ADD_FACILITY_DETAILS.CITY] as string
                        : undefined
                    }
                  />

                  <Input
                    // disabled
                    textFieldLabel="State"
                    name={ADD_FACILITY_DETAILS.STATE}
                    value={values[ADD_FACILITY_DETAILS.STATE]}
                    onChangeCb={(e) => {
                      setFieldValue(ADD_FACILITY_DETAILS.STATE, e.target.value);
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.STATE] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.STATE])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.STATE]
                        ? errors[ADD_FACILITY_DETAILS.STATE] as string
                        : undefined
                    }
                  />

                  {/* <DatePicker
                    name={ADD_FACILITY_DETAILS.DATE_OF_BIRTH}
                    value={values[ADD_FACILITY_DETAILS.DATE_OF_BIRTH]}
                    onChangeCb={(value) => {
                      console.log("value:", value);

                      setFieldValue(
                        ADD_FACILITY_DETAILS.DATE_OF_BIRTH,
                        value
                      );
                    }}
                    isStarPresent={true}
                    label="Date Of Birth"
                    style={{ width: "100%" }}
                    maxDate={moment()}
                    error={
                      touched[ADD_FACILITY_DETAILS.DATE_OF_BIRTH] &&
                      Boolean(
                        errors[ADD_FACILITY_DETAILS.DATE_OF_BIRTH]
                      )
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.DATE_OF_BIRTH]
                        ? errors[ADD_FACILITY_DETAILS.DATE_OF_BIRTH]
                        : undefined
                    }
                  /> */}

                  <Input
                    // disabled
                    textFieldLabel="Country"
                    name={ADD_FACILITY_DETAILS.COUNTRY}
                    value={values[ADD_FACILITY_DETAILS.COUNTRY]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_FACILITY_DETAILS.COUNTRY,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.COUNTRY] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.COUNTRY])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.COUNTRY]
                        ? errors[ADD_FACILITY_DETAILS.COUNTRY] as string
                        : undefined
                    }
                  />

                  <Input
                    // disabled
                    textFieldLabel="Zip Code"
                    name={ADD_FACILITY_DETAILS.ZIP_CODE}
                    value={values[ADD_FACILITY_DETAILS.ZIP_CODE]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_FACILITY_DETAILS.ZIP_CODE,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_FACILITY_DETAILS.ZIP_CODE] &&
                      Boolean(errors[ADD_FACILITY_DETAILS.ZIP_CODE])
                    }
                    helperText={
                      touched[ADD_FACILITY_DETAILS.ZIP_CODE]
                        ? errors[ADD_FACILITY_DETAILS.ZIP_CODE] as string
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-10 gap-4">
              {/* <Button
                label={TEXT_CONSTANTS?.CANCEL}
                variant="outlined"
                onClickCb={() => {}}
                style={{
                  width: "120px",
                  height: "44px",
                  padding: "12px 20px",
                  fontSize: "16px",
                  backgroundColor: "white",
                  border: "none",
                  color: "#393939",
                }}
              /> */}
              <Button
                // disabled={!isValid} // Button disabled if form is invalid or submitting
                type="submit"
                label={TEXT_CONSTANTS?.SAVE}
                variant="contained"
                onClickCb={() => {}}
                style={{
                  width: "200px",
                  height: "44px",
                  padding: "12px 20px",
                  fontSize: "16px",
                }}
              ></Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

// ADD FACILITY DIALOG

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    BorderAllRounded: "8px",
    Height: "436px",
    width: "708px", // Set your custom width here
    maxWidth: "none", // Prevents Material-UI from overriding your width
  },
}));

interface AddNewFacilityDialogProps {
  isOpen: boolean;
  defaultFacilityData: any;
  dialogHeading: string;
  handleClose: () => void;
  handleAddFacilityData: (data: any) => void;
}

export default function AddNewFacilityDialog({
  isOpen = false,
  defaultFacilityData,
  handleClose,
  handleAddFacilityData,
  dialogHeading = "Your Heading Here...",
}: AddNewFacilityDialogProps) {
  return (
    <React.Fragment>
      <BootstrapDialog
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="customized-dialog-title"
        open={isOpen}
      >
        <div
          className="flex justify-between items-center rounded-lg"
          style={{ margin: "14px 24px" }}
        >
          <Heading title={dialogHeading} />
          <div onClick={handleClose} className="cursor-pointer">
            <CloseIcon />
          </div>
        </div>

        <DialogContent
          dividers
          style={{ padding: "16px 24px 24px 24px", borderRadius: "8px" }}
        >
          <AddFacility
            defaultSelectedData={defaultFacilityData}
            handleOnSubmittedData={(formData) => {
              handleAddFacilityData(formData);
            }}
          />
        </DialogContent>
      </BootstrapDialog>
    </React.Fragment>
  );
}
